import { useEffect, useState } from "react";
import "../styles/ApplicationDetailDrawer.css";
import "../styles/ApplicationVisibilityPanel.css";
import Skeleton from "../components/common/Skeleton";
import FadeIn from "../components/common/FadeIn";
import api from "../../shared/AxiosConfig";
import { tEnum } from "../../config/translations";
import { toast } from "react-toastify";
import { patchApplicationSettings } from "../../core/api/ApplicationSettingsApi";

const FIELD_LABELS = {
    name: "Dönem Adı",
    code: "Kod",
    startDate: "Başlangıç Tarihi",
    endDate: "Bitiş Tarihi",
    enrollmentLimit: "Öğrenci Limiti",
    note: "Not",
    type: "Tip",
};

const ERROR_TYPE_MAP = {
    "any.required": "zorunludur",
    "string.min": "en az karakter olmalıdır",
    "string.max": "en fazla karakter olmalıdır",
    "date.min": "bitiş tarihi başlangıçtan önce olamaz",
};

export default function TermDetailDrawer({ open, onClose, data, loading }) {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [applicationOpen, setApplicationOpen] = useState(false);

    const emptyForm = {
        name: "",
        code: "",
        startDate: "",
        endDate: "",
        enrollmentLimit: "",
        note: "",
        type: "PHYSICAL",
    };

    const [form, setForm] = useState(emptyForm);
    const [originalForm, setOriginalForm] = useState(emptyForm);

    const detail = data;
    const isCreateMode = data === null;

    useEffect(() => {
        if (open) {
            setMounted(true);
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setVisible(true))
            );
        } else {
            setVisible(false);
            setTimeout(() => setMounted(false), 380);
        }
        setEditMode(isCreateMode);
        setForm(emptyForm);
        setOriginalForm(emptyForm);
    }, [open, isCreateMode]);

    useEffect(() => {
        if (isCreateMode) {
            setForm(emptyForm);
            setOriginalForm(emptyForm);
            setEditMode(true);
            return;
        }
        if (!detail) return;

        const startDate = detail.startDate
            ? new Date(detail.startDate).toISOString().slice(0, 10)
            : "";
        const endDate = detail.endDate
            ? new Date(detail.endDate).toISOString().slice(0, 10)
            : "";

        const loaded = {
            name: detail.name || "",
            code: detail.code || "",
            startDate,
            endDate,
            enrollmentLimit: detail.enrollmentLimit ?? "",
            note: detail.note || "",
            type: detail.type || "PHYSICAL",
        };

        setForm(loaded);
        setOriginalForm(loaded);
        setApplicationOpen(!!detail.isApplicationOpen);
        setEditMode(false);
    }, [detail, isCreateMode]);

    const handleApplicationOpenToggle = async () => {
        if (!detail?.isDefault) return;
        setActionLoading(true);
        const next = !applicationOpen;
        try {
            const res = await patchApplicationSettings(next);
            setApplicationOpen(!!res?.data?.isApplicationOpen);
            toast.success(res?.message || (next ? "Başvuru formu açıldı" : "Başvuru formu kapatıldı"));
            onClose(true);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Güncellenemedi");
        } finally {
            setActionLoading(false);
        }
    };

    if (!mounted) return null;

    const formatDate = (d) =>
        d
            ? new Date(d).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            })
            : "-";

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        // Frontend validation
        const name = form.name?.trim();
        const code = form.code?.trim();
        const startDate = form.startDate;
        const endDate = form.endDate;

        if (!name) {
            toast.error("Dönem Adı zorunludur");
            return;
        }
        if (!code) {
            toast.error("Kod zorunludur");
            return;
        }
        if (!startDate) {
            toast.error("Başlangıç Tarihi zorunludur");
            return;
        }
        if (!endDate) {
            toast.error("Bitiş Tarihi zorunludur");
            return;
        }
        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
            toast.error("Bitiş tarihi başlangıç tarihinden önce olamaz");
            return;
        }

        try {
            const payload = {
                name,
                code,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                enrollmentLimit: form.enrollmentLimit
                    ? Number(form.enrollmentLimit)
                    : null,
                note: form.note?.trim() || null,
                type: form.type,
            };

            if (isCreateMode) {
                await api.post("/terms", payload);
                toast.success("Dönem oluşturuldu");
            } else {
                await api.patch(`/terms/${detail.publicId}`, payload);
                toast.success("Dönem güncellendi");
            }

            setEditMode(false);
            onClose(true);
        } catch (e) {
            const details = e?.response?.data?.details;

            if (Array.isArray(details) && details.length > 0) {
                details.forEach((err) => {
                    const fieldName =
                        FIELD_LABELS[err.field] ||
                        err.context?.label ||
                        err.field ||
                        "Alan";
                    const typeMsg =
                        ERROR_TYPE_MAP[err.type] ||
                        err.message ||
                        "geçersiz";
                    toast.error(`${fieldName} ${typeMsg}`);
                });
            } else {
                const msg =
                    e?.response?.data?.message ||
                    e?.response?.data?.details?.[0]?.message ||
                    "İşlem başarısız";
                toast.error(msg);
            }
        }
    };

    const handleSetActive = async () => {
        if (!detail?.publicId || detail.isDefault) return;
        setActionLoading(true);
        try {
            await api.patch(`/terms/${detail.publicId}/set-active`);
            toast.success(`"${detail.name}" aktif dönem olarak ayarlandı`);
            onClose(true);
        } catch (e) {
            toast.error(e?.response?.data?.message || "İşlem başarısız");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (
            !window.confirm(
                `"${detail.name}" dönemini silmek istediğinize emin misiniz?`
            )
        )
            return;
        setActionLoading(true);
        try {
            await api.delete(`/terms/${detail.publicId}`);
            toast.success("Dönem silindi");
            onClose(true);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Silme başarısız");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <>
            <div
                className={`drawer-overlay ${visible ? "show" : ""}`}
                onClick={() => onClose(false)}
            />

            <div className={`detail-drawer ${visible ? "open" : ""}`}>
                <div className="drawer-header">
                    <h3>
                        {isCreateMode ? "Yeni Dönem" : "Dönem Detayı"}
                    </h3>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        {!isCreateMode && !editMode && (
                            <>
                                {!detail?.isDefault && (
                                    <button
                                        className="drawer-icon-btn"
                                        onClick={handleSetActive}
                                        disabled={actionLoading}
                                        title="Aktif Yap"
                                    >
                                        <i className="bi bi-check-circle"></i>
                                    </button>
                                )}
                                <button
                                    className="drawer-icon-btn"
                                    onClick={() => setEditMode(true)}
                                    title="Düzenle"
                                >
                                    <i className="bi bi-pencil-square"></i>
                                </button>
                                <button
                                    className="drawer-icon-btn"
                                    onClick={handleDelete}
                                    disabled={actionLoading}
                                    title="Sil"
                                    style={{ color: "var(--valtekError, #e74c3c)" }}
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </>
                        )}

                        {!isCreateMode && editMode && (
                            <>
                                <button
                                    className="drawer-icon-btn"
                                    title="Kaydet"
                                    onClick={handleSave}
                                >
                                    <i className="bi bi-check2-circle"></i>
                                </button>
                                <button
                                    className="drawer-icon-btn"
                                    title="İptal"
                                    onClick={() => setEditMode(false)}
                                >
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                </button>
                            </>
                        )}

                        {isCreateMode && (
                            <button
                                className="drawer-icon-btn"
                                title="Kaydet"
                                onClick={handleSave}
                            >
                                <i className="bi bi-check2-circle"></i>
                            </button>
                        )}

                        <button
                            className="drawer-close"
                            onClick={() => onClose(false)}
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="drawer-body">
                    {!isCreateMode && (
                        <FadeIn delay={0.05}>
                            <div className="drawer-section">
                                <div className="section-title">Durum</div>
                                {loading ? (
                                    <Skeleton height={40} />
                                ) : (
                                    <>
                                        <span
                                            className={`status-badge ${
                                                detail?.isDefault
                                                    ? "badge-approve"
                                                    : "badge-new"
                                            }`}
                                        >
                                            {detail?.isDefault ? "Aktif" : "Pasif"}
                                        </span>

                                        {detail?.isDefault && (
                                            <div className="term-app-visibility">
                                                <div className="term-app-visibility-row">
                                                    <span className="term-app-visibility-label">
                                                        Public başvuru formu
                                                    </span>
                                                    <div className="app-visibility-actions">
                                                        <span
                                                            className={`app-visibility-status ${
                                                                applicationOpen ? "open" : "closed"
                                                            }`}
                                                        >
                                                            {applicationOpen ? "Açık" : "Kapalı"}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={`app-visibility-toggle ${
                                                                applicationOpen ? "on" : "off"
                                                            }`}
                                                            role="switch"
                                                            aria-checked={applicationOpen}
                                                            disabled={actionLoading}
                                                            onClick={handleApplicationOpenToggle}
                                                            title={
                                                                applicationOpen
                                                                    ? "Formu kapat"
                                                                    : "Formu aç"
                                                            }
                                                        >
                                                            <span className="app-visibility-knob" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="term-app-visibility-hint">
                                                    Form tipi: {tEnum("TERM_TYPE", detail?.type)}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </FadeIn>
                    )}

                    <FadeIn delay={0.1}>
                        <div className="drawer-section">
                            <div className="section-title">Dönem Bilgileri</div>
                            <div className="info-grid single-column">
                                <label>Dönem Adı</label>
                                <input
                                    disabled={!editMode}
                                    value={form?.name}
                                    onChange={(e) =>
                                        handleChange("name", e.target.value)
                                    }
                                    className="drawer-input"
                                />

                                <label>Kod</label>
                                <input
                                    disabled={!editMode}
                                    value={form?.code}
                                    onChange={(e) =>
                                        handleChange("code", e.target.value)
                                    }
                                    className="drawer-input"
                                />

                                <label>Başlangıç Tarihi</label>
                                <input
                                    type="date"
                                    disabled={!editMode}
                                    value={form?.startDate}
                                    onChange={(e) =>
                                        handleChange("startDate", e.target.value)
                                    }
                                    className="drawer-input"
                                />

                                <label>Bitiş Tarihi</label>
                                <input
                                    type="date"
                                    disabled={!editMode}
                                    value={form?.endDate}
                                    onChange={(e) =>
                                        handleChange("endDate", e.target.value)
                                    }
                                    className="drawer-input"
                                />

                                <label>Öğrenci Limiti</label>
                                <input
                                    type="number"
                                    min={0}
                                    disabled={!editMode}
                                    value={form?.enrollmentLimit}
                                    onChange={(e) =>
                                        handleChange(
                                            "enrollmentLimit",
                                            e.target.value
                                        )
                                    }
                                    className="drawer-input"
                                    placeholder="Boş bırakılabilir"
                                />

                                <label>Tip</label>
                                <select
                                    disabled={!editMode}
                                    value={form?.type}
                                    onChange={(e) =>
                                        handleChange("type", e.target.value)
                                    }
                                    className="drawer-input"
                                >
                                    <option value="PHYSICAL">
                                        {tEnum("TERM_TYPE", "PHYSICAL")}
                                    </option>
                                    <option value="ONLINE">
                                        {tEnum("TERM_TYPE", "ONLINE")}
                                    </option>
                                </select>

                                <label>Not</label>
                                <textarea
                                    disabled={!editMode}
                                    value={form?.note}
                                    onChange={(e) =>
                                        handleChange("note", e.target.value)
                                    }
                                    className="drawer-input"
                                    placeholder="Opsiyonel not..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </FadeIn>

                    {!isCreateMode && !editMode && (
                        <FadeIn delay={0.15}>
                            <div className="drawer-section">
                                <div className="section-title">Özet</div>
                                <div className="info-grid single-column">
                                    <div>
                                        <strong>Kayıtlı Öğrenci:</strong>{" "}
                                        {detail?.enrollmentCount ?? 0}
                                    </div>
                                </div>
                            </div>
                        </FadeIn>
                    )}
                </div>
            </div>
        </>
    );
}
